import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Typography, Alert, Spin } from 'antd';
import { 
  DollarOutlined, 
  TeamOutlined, 
  ApiOutlined, 
  WarningOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title } = Typography;

interface DashboardStats {
  total_tenants: number;
  active_tenants: number;
  total_credit_balance: number;
  total_credits_consumed_today: number;
  total_credits_consumed_month: number;
  active_api_keys: number;
  recent_alerts: Array<{
    tenant_id: string;
    tenant_name: string;
    threshold: number;
    current_balance: number;
    created_at: string;
  }>;
  top_consumers: Array<{
    tenant_id: string;
    tenant_name: string;
    credits_consumed: number;
    current_balance: number;
  }>;
}

const SuperAdminDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/super-admin/dashboard/stats');
      setStats(response.data.stats);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  if (!stats) {
    return <Alert message="No data available" type="info" showIcon />;
  }

  const alertColumns = [
    {
      title: 'Tenant',
      dataIndex: 'tenant_name',
      key: 'tenant_name',
    },
    {
      title: 'Threshold',
      dataIndex: 'threshold',
      key: 'threshold',
      render: (threshold: number) => (
        <Tag color={threshold <= 5 ? 'red' : threshold <= 25 ? 'orange' : 'yellow'}>
          {threshold}%
        </Tag>
      ),
    },
    {
      title: 'Balance',
      dataIndex: 'current_balance',
      key: 'current_balance',
      render: (balance: number) => (
        <span style={{ color: balance < 100 ? '#ff4d4f' : 'inherit' }}>
          {balance != null ? balance.toLocaleString() : 0} credits
        </span>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  const consumerColumns = [
    {
      title: 'Tenant',
      dataIndex: 'tenant_name',
      key: 'tenant_name',
    },
    {
      title: 'Consumed',
      dataIndex: 'credits_consumed',
      key: 'credits_consumed',
      render: (credits: number) => (
        <span style={{ fontWeight: 'bold' }}>{credits != null ? credits.toLocaleString() : 0}</span>
      ),
      sorter: (a: any, b: any) => b.credits_consumed - a.credits_consumed,
    },
    {
      title: 'Balance',
      dataIndex: 'current_balance',
      key: 'current_balance',
      render: (balance: number) => `${balance != null ? balance.toLocaleString() : 0} credits`,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Super Admin Dashboard</Title>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Tenants"
              value={stats.total_tenants}
              prefix={<TeamOutlined />}
              suffix={
                <span style={{ fontSize: '14px', color: '#52c41a' }}>
                  ({stats.active_tenants} active)
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Credit Balance"
              value={stats.total_credit_balance}
              prefix={<DollarOutlined />}
              suffix="credits"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Credits Consumed Today"
              value={stats.total_credits_consumed_today}
              prefix={<ArrowDownOutlined />}
              suffix="credits"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active API Keys"
              value={stats.active_api_keys}
              prefix={<ApiOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24} lg={12}>
          <Card>
            <Statistic
              title="Credits Consumed This Month"
              value={stats.total_credits_consumed_month}
              prefix={<ArrowUpOutlined />}
              suffix="credits"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title={<><WarningOutlined style={{ color: '#faad14' }} /> Recent Credit Alerts</>}>
            {stats.recent_alerts && stats.recent_alerts.length > 0 ? (
              <Table
                columns={alertColumns}
                dataSource={stats.recent_alerts}
                pagination={false}
                size="small"
                rowKey={(record) => `${record.tenant_id}-${record.created_at}`}
              />
            ) : (
              <Alert message="No recent alerts" type="success" showIcon />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Top Credit Consumers">
            {stats.top_consumers && stats.top_consumers.length > 0 ? (
              <Table
                columns={consumerColumns}
                dataSource={stats.top_consumers}
                pagination={false}
                size="small"
                rowKey="tenant_id"
              />
            ) : (
              <Alert message="No consumption data" type="info" showIcon />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminDashboard;
